import React, { useState, useEffect } from 'react';
import './DateTimePicker.css';

export default function DateTimePicker({ value, onChange }) {
  const parseValue = (val) => {
    if (!val) return { date: '', hour: '12', minute: '00', period: 'AM' };
    const d = new Date(val);
    if (isNaN(d.getTime())) return { date: '', hour: '12', minute: '00', period: 'AM' };
    
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    
    let h = d.getHours();
    const min = String(d.getMinutes()).padStart(2, '0');
    const period = h >= 12 ? 'PM' : 'AM';
    h = h % 12;
    if (h === 0) h = 12;
    const hourStr = String(h).padStart(2, '0');

    return { date: `${y}-${m}-${day}`, hour: hourStr, minute: min, period };
  };

  const [state, setState] = useState(() => parseValue(value));

  // Sync external changes (like clearing)
  useEffect(() => {
    const parsed = parseValue(value);
    if (parsed.date !== state.date) {
      setState(parsed);
    }
    // We don't strictly sync hour/minute if date hasn't changed to avoid interrupting typing.
  }, [value]);

  const fireChange = (s) => {
    if (!s.date) {
      onChange('');
      return;
    }
    let h24 = parseInt(s.hour || '12', 10);
    if (isNaN(h24)) h24 = 12;
    if (h24 > 12) h24 = 12;
    if (h24 < 1) h24 = 1;

    let m = parseInt(s.minute || '0', 10);
    if (isNaN(m)) m = 0;
    if (m > 59) m = 59;
    if (m < 0) m = 0;

    if (s.period === 'PM' && h24 < 12) h24 += 12;
    if (s.period === 'AM' && h24 === 12) h24 = 0;
    
    const localStr = `${s.date}T${String(h24).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
    const d = new Date(localStr);
    if (!isNaN(d.getTime())) {
      onChange(d.toISOString());
    }
  };

  const handleInputChange = (field, val) => {
    setState(prev => {
      const next = { ...prev, [field]: val };
      fireChange(next);
      return next;
    });
  };

  const handleBlur = (field) => {
    setState(prev => {
      const next = { ...prev };
      if (field === 'hour') {
        let h = parseInt(next.hour, 10);
        if (isNaN(h) || h < 1) h = 12;
        if (h > 12) h = 12;
        next.hour = String(h).padStart(2, '0');
      }
      if (field === 'minute') {
        let m = parseInt(next.minute, 10);
        if (isNaN(m) || m < 0) m = 0;
        if (m > 59) m = 59;
        next.minute = String(m).padStart(2, '0');
      }
      return next;
    });
  };

  return (
    <div className="cdt-container">
      <input 
        type="date" 
        className="cdt-input cdt-date" 
        value={state.date} 
        onChange={(e) => handleInputChange('date', e.target.value)} 
        title="Date"
      />
      <div className="cdt-time">
        <span className="cdt-label">Hr:</span>
        <input 
          type="number" 
          min="1" 
          max="12" 
          className="cdt-input cdt-num" 
          value={state.hour}
          onChange={(e) => handleInputChange('hour', e.target.value)}
          onBlur={() => handleBlur('hour')}
          title="Hour"
        />
        <span className="cdt-colon">:</span>
        <span className="cdt-label">Min:</span>
        <input 
          type="number" 
          min="0" 
          max="59" 
          className="cdt-input cdt-num" 
          value={state.minute}
          onChange={(e) => handleInputChange('minute', e.target.value)}
          onBlur={() => handleBlur('minute')}
          title="Minute"
        />
        <select 
          className="cdt-input cdt-select" 
          value={state.period} 
          onChange={(e) => handleInputChange('period', e.target.value)}
          title="AM/PM"
        >
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
}
